/**
 * ShopAll Product HF - Subscription toggle functionality for Hello Fresh
 * Handles monthly/one-time purchase option switching for Hello Fresh product forms
 */

document.addEventListener("DOMContentLoaded", function () {
  // Configuration object for maintainability
  const CONFIG = {
    selectors: {
      productForm: ".product-form.cta-container",
      productTypeInput: 'input[name^="product-type"]',
      sellingPlanInput: (handle) => `#${handle}-selling_plan`,
      monthlyLabel: (handle) => `label.unit203-ryze-hf-product-price[for="${handle}-monthly"]`,
      oneTimeLabel: (handle) => `label.unit203-ryze-hf-product-price[for="${handle}-one_time"]`,
      productContainer: ".unit203-ryze-hf-shopall-product-container"
    },
    classes: {
      selected: "selected"
    }
  };

  /**
   * Extract product handle from form input name
   * @param {HTMLElement} form - The product form element
   * @returns {string|null} Product handle or null if not found
   */
  function getProductHandle(form) {
    try {
      const input = form.querySelector(CONFIG.selectors.productTypeInput);
      if (!input?.name) return null;

      const match = input.name.match(/product-type-(.+)/);
      return match ? match[1] : null;
    } catch (error) {
      console.warn("Failed to extract product handle:", error);
      return null;
    }
  }

  /**
   * Get required DOM elements for a product form
   * @param {HTMLElement} form - The product form element
   * @param {string} productHandle - The product handle
   * @returns {Object} Object containing DOM elements
   */
  function getFormElements(form, productHandle) {
    return {
      monthlyLabel: form.querySelector(CONFIG.selectors.monthlyLabel(productHandle)),
      oneTimeLabel: form.querySelector(CONFIG.selectors.oneTimeLabel(productHandle)),
      sellingPlanInput: form.querySelector(CONFIG.selectors.sellingPlanInput(productHandle))
    };
  }

  /**
   * Toggle selected states between monthly and one-time options
   * @param {HTMLElement} activeLabel - Label to make active
   * @param {HTMLElement} inactiveLabel - Label to make inactive
   * @param {HTMLElement} sellingPlanInput - Selling plan input element
   * @param {string} defaultSellingPlanId - Default selling plan ID
   */
  function toggleSelectedState(activeLabel, inactiveLabel, sellingPlanInput, defaultSellingPlanId) {
    if (!activeLabel || !inactiveLabel) {
      console.warn("Missing label elements for toggle operation");
      return;
    }

    // Update active label - add selected class
    activeLabel.classList.add(CONFIG.classes.selected);

    // Update inactive label - remove selected class
    inactiveLabel.classList.remove(CONFIG.classes.selected);

    // Update selling plan based on selection
    if (sellingPlanInput) {
      const isMonthly = activeLabel.querySelector('input[type="radio"]')?.id.includes('monthly');
      sellingPlanInput.value = isMonthly ? (defaultSellingPlanId || "") : "";
    }
  }

  /**
   * Initialize a single product form
   * @param {HTMLElement} form - The product form element
   */
  function initializeProductForm(form) {
    const productHandle = getProductHandle(form);
    if (!productHandle) {
      console.warn("Could not extract product handle from form");
      return;
    }

    const elements = getFormElements(form, productHandle);
    const { monthlyLabel, oneTimeLabel, sellingPlanInput } = elements;

    // Skip if required elements are missing
    if (!monthlyLabel && !oneTimeLabel) {
      console.warn(`No subscription options found for product: ${productHandle}`);
      return;
    }

    const defaultSellingPlanId = sellingPlanInput?.value;

    // Subscription toggle logic
    const createClickHandler = (activeLabel, inactiveLabel) => {
      return function(event) {
        // Don't prevent default - let radio button work naturally
        toggleSelectedState(activeLabel, inactiveLabel, sellingPlanInput, defaultSellingPlanId);
      };
    };

    // Add event listeners
    if (monthlyLabel) {
      monthlyLabel.addEventListener("click", createClickHandler(monthlyLabel, oneTimeLabel));
    }

    if (oneTimeLabel) {
      oneTimeLabel.addEventListener("click", createClickHandler(oneTimeLabel, monthlyLabel));
    }

    // Set initial state based on checked radio button
    const monthlyRadio = monthlyLabel?.querySelector('input[type="radio"]');
    const oneTimeRadio = oneTimeLabel?.querySelector('input[type="radio"]');

    if (monthlyRadio?.checked) {
      monthlyLabel.classList.add(CONFIG.classes.selected);
      oneTimeLabel?.classList.remove(CONFIG.classes.selected);
    } else if (oneTimeRadio?.checked) {
      oneTimeLabel.classList.add(CONFIG.classes.selected);
      monthlyLabel?.classList.remove(CONFIG.classes.selected);
    }
  }

  // Initialize all product forms
  const productForms = document.querySelectorAll(CONFIG.selectors.productForm);

  if (productForms.length === 0) {
    console.info("No product forms found on page");
    return;
  }

  productForms.forEach((form, index) => {
    try {
      initializeProductForm(form);
    } catch (error) {
      console.error(`Failed to initialize product form ${index}:`, error);
    }
  });

  console.info(`Initialized ${productForms.length} Hello Fresh product form(s)`);
});
